/**
 * Tier-aware action helpers — single source of truth for "what happens when a
 * user clicks consultation / unlock-doc / new-thread" given their subscription
 * state and the thread's `eligible_offers` payload.
 *
 * Replaces the maze of branches at src/pages/helpers.js lines 1397-1977.
 * Callers should be thin: read metadata + offers → delegate → dispatch.
 *
 * Per Group 0.5 corrections (BE-shipped 4-tier contract):
 *   - `tierOf()` returns the canonical tier identity using BE wire values
 *     (`free | pro | counsel | unlimited | unknown`); pre_onboarding maps to free.
 *
 * Per amended legal-review-access spec (2026-05-23, tasks 11.E.3–11.E.4):
 *   - The "Loop in Counsel" CTA is no longer a branching point. Every CTA
 *     click mounts the LegalReviewShelf as the universal first stop. Pre-shelf
 *     call sites SHALL NOT call `resolveConsultationAction`; they just dispatch
 *     `setRequestLegalReviewAsideDetails`.
 *   - `resolveConsultationAction` now resolves the matrix decision for the
 *     in-shelf submit click ("Loop in my Inhouse Counsel"). Arms:
 *       - `signup_redirect`  — anonymous user; trigger Clerk signup flow.
 *       - `submit_referral`  — Counsel/Unlimited with credits; POST referral.
 *       - `direct_checkout`  — Free/Pro → Counsel checkout. Caller dispatches
 *         `openWithEntry({ entry: "consultation", selectedTier: action.tier,
 *         initialStep: "checkout" })`.
 *       - `not_available`    — out-of-scope; defensive no-op.
 *
 * Per `additional-consultation-one-time-charge` (2026-08-19): both
 * credits-exhausted rows (`counsel` and `unlimited`) collapse to a single new
 * arm, `purchase_additional_consultation` — a $149 one-time purchase.
 * Previously `counsel`-exhausted upsold to the `inhouse_counsel` ($499)
 * subscription, which Pricing 3.5 retires (nobody can buy it any more); and
 * `unlimited`-exhausted opened `ConsultationResetModal`, a dead end with no
 * purchase path. Caller dispatches `openWithEntry({ entry: "consultation",
 * purchaseTarget: { kind: "one_time", transactionType: "legal_consultation" },
 * initialStep: "checkout" })`. See
 * `openspec/changes/additional-consultation-one-time-charge/`.
 *
 * Per task 11.E.7, `getPlanPickerVariant` now accepts an `entry` second arg.
 * When `entry === 'consultation'` and the user is Free / pre_onboarding, the
 * variant collapses to `'free_consultation_direct'` — a signal to the
 * orchestrator (`openWithEntry`) to skip the picker and land directly on the
 * Counsel checkout slide. `pricing_nav` still routes through the existing
 * `'free'` (3-card) variant; all other entries preserve prior behavior.
 */
import {
  openWithEntry,
  openAt,
  type CheckoutEntry,
  type CheckoutStep,
  type CheckoutTier,
} from "src/store/slices/checkoutFlowSlice";
import { Plan } from "src/domain/plan";
import { ConsultationOffer, Entitlements } from "src/domain/entitlements";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface TierMetadata {
  subscription_type?: string | null;
  legal_review_credits_remaining?: number;
}

export interface EligibleOffersInput {
  lawyer_consultation?: string | null | false;
}

export type Tier = "free" | "pro" | "counsel" | "unlimited" | "unknown";

/**
 * `'free'` is the consultation/locked-doc/etc. variant that renders Pro + Counsel
 * (Free→Counsel exception per design decision #11). `'pro_upgrade'` and
 * `'counsel_upgrade'` are signals to the orchestrator (`openWithEntry` thunk) to
 * skip the picker entirely and land directly at Checkout with the right tier
 * preselected (design decision #15).
 *
 * `'all_plans'` is the sidenav-`pricing_nav` variant: render ALL three tiers
 * (Unlimited Documents / Lawyer-Backed / Lawyer-Backed Plus) regardless of current tier,
 * with the user's current plan marked as "Your Current Plan" (disabled CTA).
 * Per `memory:project_sidenav_upgrade_button` + ss-10.png.
 *
 * `'free_consultation_direct'` (added 2026-05-23, task 11.E.7) collapses the
 * Free-tier consultation-entry path to a single picker-skip: openWithEntry
 * auto-fills `selectedTier: 'subscriber_enterprise'` + `initialStep: 'checkout'`
 * so the user lands on the Counsel checkout slide. PlanPicker SHALL NOT mount
 * for this variant.
 */
export type PlanPickerVariant = "free" | "pro_upgrade" | "counsel_upgrade" | "all_plans" | "free_consultation_direct";

/**
 * Post-shelf-submit decision. Resolved at click time on the in-shelf "Loop in
 * my Inhouse Counsel" CTA. See file-header comment for the matrix.
 */
export type ConsultationAction =
  | { type: "signup_redirect" }
  | { type: "submit_referral" }
  | { type: "direct_checkout"; tier: CheckoutTier }
  | { type: "purchase_additional_consultation" }
  | { type: "not_available" };

interface OpenCheckoutPayload {
  entry: CheckoutEntry;
  threadId?: string | null;
  initialStep?: CheckoutStep;
  selectedTier?: CheckoutTier;
  lockedDocTitle?: string | null;
}

type Dispatch = (action: unknown) => unknown;

// ─── Helpers ─────────────────────────────────────────────────────────────────

export const tierOf = (metadata: TierMetadata | null | undefined): Tier => {
  // Legacy label mapping, derived from the Plan domain model instead of raw
  // wire-string comparison. Anonymous/unknown plans fall through to "unknown".
  const plan = Plan.from(metadata?.subscription_type);
  if (plan.isFree()) return "free";
  if (plan.isRank(1)) return "pro"; // $49 subscriber_business
  if (plan.isRank(2)) return "counsel"; // $149 subscriber_enterprise
  if (plan.isRank(3)) return "unlimited"; // retired $499 inhouse_counsel
  return "unknown";
};

const hasCredits = (metadata: TierMetadata | null | undefined): boolean =>
  Entitlements.from(metadata).hasConsultationCredits();

const isOutOfScope = (offers: EligibleOffersInput | null | undefined): boolean =>
  !ConsultationOffer.from(offers).isAvailable();

const isAnonymous = (metadata: TierMetadata | null | undefined): boolean => !metadata || !metadata.subscription_type;

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Compute the picker variant for the current user, optionally specialized for
 * the entry that opened the flow.
 *
 * - `entry === 'pricing_nav'` always uses the 3-card variant when computed by
 *   the caller (CheckoutFlowModal hard-codes `'all_plans'` for this entry; the
 *   helper preserves the per-tier picker shape here for callers that don't
 *   need the 3-card layout).
 * - `entry === 'consultation'` + Free / pre_onboarding → `'free_consultation_direct'`
 *   (picker-skip; see openWithEntry auto-fill).
 * - All other paths preserved (one-step jumps per design decision #11).
 *
 * Returns `null` for tiers with no upgrade path (Unlimited).
 */
export const getPlanPickerVariant = (
  metadata: TierMetadata | null | undefined,
  entry: CheckoutEntry | null = null,
): PlanPickerVariant | null => {
  const plan = Plan.from(metadata?.subscription_type);
  // INH-5059: paid users with no upgrade path (Counsel $149 is the top
  // purchasable tier; Plus $499 is retired) get no picker — the orchestrator
  // shows AlreadySubscribedModal instead. `Plan.hasUpgradePath()` is the
  // single home of this rule.
  if (plan.isPaid() && !plan.hasUpgradePath()) return null;
  if (plan.isFree()) {
    if (entry === "consultation") return "free_consultation_direct";
    return "free";
  }
  if (plan.isRank(1)) return "pro_upgrade";
  // Anonymous / unknown — no picker; callers route to signup.
  return null;
};

/**
 * Resolve the in-shelf submit-click outcome for the consultation CTA.
 *
 * Precedence: anonymous > out-of-scope > tier × credits matrix.
 */
export const resolveConsultationAction = (
  metadata: TierMetadata | null | undefined,
  eligibleOffers: EligibleOffersInput | null | undefined,
): ConsultationAction => {
  if (isAnonymous(metadata)) return { type: "signup_redirect" };
  // if (isOutOfScope(eligibleOffers)) return { type: "not_available" };

  const tier = tierOf(metadata);

  if (tier === "free" || tier === "pro") {
    return { type: "direct_checkout", tier: "subscriber_enterprise" };
  }

  // Both Counsel ($149) and legacy Unlimited ($499) route to the same $149
  // one-time purchase once their included credit is exhausted — see the
  // additional-consultation-one-time-charge file-header note above.
  if (tier === "counsel" || tier === "unlimited") {
    return hasCredits(metadata) ? { type: "submit_referral" } : { type: "purchase_additional_consultation" };
  }

  return { type: "not_available" };
};

/**
 * Thin wrapper around the `openWithEntry` thunk. Kept for parity with the
 * legacy `openCheckoutForEntry` call sites; new callers MAY dispatch the thunk
 * directly.
 */
export const openCheckoutForEntry = (dispatch: Dispatch, payload: OpenCheckoutPayload): void => {
  dispatch(openWithEntry(payload));
};

// Re-export the pure reducer so tests that need to inspect dispatched actions
// without going through the thunk can import from here.
export { openAt };
